import type {
  AgeBand,
  AnaestheticCase,
  AnaestheticCaseInput,
  AnaestheticPriority,
  AnaestheticSetting,
  AsaGrade,
  CapabilityLevel,
  HilloNumber,
  LearnerGrade,
} from '@iculogbook/shared';

/**
 * The state behind the anaesthetic case editor, and the pure functions around
 * it. Same discipline as the other forms: an untouched optional field becomes
 * an **absent** property rather than an empty string or a zero.
 *
 * There is no `initialValues` here, and deliberately. Anaesthetic cases are
 * imported from the RCoA platform and never created in this app, so the only
 * way into this form is from a stored record.
 */

export type AnaestheticFormValues = {
  setting: AnaestheticSetting;
  title: string;
  occurredAt: Date;
  ageBand: AgeBand | null;
  capabilityLevel: CapabilityLevel | null;
  priority: AnaestheticPriority | null;
  asa: AsaGrade | null;
  teachingGrades: readonly LearnerGrade[];
  hillos: readonly HilloNumber[];
  note: string;
};

export function anaestheticValuesFrom(record: AnaestheticCase): AnaestheticFormValues {
  return {
    setting: record.setting,
    title: record.title,
    occurredAt: record.occurredAt,
    ageBand: record.ageBand,
    capabilityLevel: record.capabilityLevel,
    priority: record.priority ?? null,
    asa: record.asa ?? null,
    teachingGrades: record.teachingGrades ?? [],
    hillos: record.hillos ?? [],
    note: record.note ?? '',
  };
}

export type MissingAnaestheticField = 'title' | 'ageBand' | 'capabilityLevel';

export function missingAnaestheticFields(
  values: AnaestheticFormValues,
): MissingAnaestheticField[] {
  const missing: MissingAnaestheticField[] = [];
  if (values.title.trim() === '') missing.push('title');
  if (values.ageBand === null) missing.push('ageBand');
  if (values.capabilityLevel === null) missing.push('capabilityLevel');
  return missing;
}

/**
 * Turns the form state into the record the repository writes, or null if a
 * required field is missing. The shared schema is still what decides validity.
 */
export function buildAnaestheticInput(
  values: AnaestheticFormValues,
): AnaestheticCaseInput | null {
  const title = values.title.trim();
  if (title === '' || values.ageBand === null || values.capabilityLevel === null) return null;

  const note = values.note.trim();

  return {
    setting: values.setting,
    title,
    occurredAt: values.occurredAt,
    ageBand: values.ageBand,
    capabilityLevel: values.capabilityLevel,
    ...(values.priority === null ? {} : { priority: values.priority }),
    ...(values.asa === null ? {} : { asa: values.asa }),
    ...(values.teachingGrades.length === 0
      ? {}
      : { teachingGrades: [...values.teachingGrades] }),
    ...(values.hillos.length === 0 ? {} : { hillos: [...values.hillos] }),
    ...(note === '' ? {} : { note }),
  };
}
